// src/components/layout/LegalPageLayout.tsx
// No changes needed in this file, as the fix is in the child component.
import React, { useState, useEffect, useRef } from 'react';
import { SideNavigation, SideNavItem } from '../../shared/SideNavigation';
import { renderIcon } from '@dopomogai/utils';

interface LegalPageLayoutProps {
  pageTitle: string;
  fullLastUpdatedText: string;
  navItems: SideNavItem[];
  children: React.ReactNode;
}

export const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({ pageTitle, fullLastUpdatedText, navItems, children }) => {
  const [activeSectionId, setActiveSectionId] = useState<string>(navItems[0]?.id || '');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (navItems.length > 0) {
      setActiveSectionId(navItems[0].id);
    }
    const observerOptions = { rootMargin: '-100px 0px -50% 0px', threshold: 0, };
    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSectionId(entry.target.id);
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const currentRefs = sectionRefs.current;
    Object.values(currentRefs).forEach(el => {
      if (el) observer.observe(el);
    });
    return () => {
      Object.values(currentRefs).forEach(el => {
        if (el) observer.unobserve(el);
      });
    };
  }, [navItems]);

  const handleNavigation = (sectionId: string) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      const yOffset = -90;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const childrenWithRefs = React.Children.map(children, child => {
    if (React.isValidElement(child) && typeof child.props.id === 'string') {
      // This cloneElement call is now valid because LegalSection is a forwardRef component.
      return React.cloneElement(child as React.ReactElement<any>, {
        ref: (el: HTMLElement | null) => { sectionRefs.current[child.props.id] = el; },
      });
    }
    return child;
  });

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 max-w-7xl mx-auto">
          <aside className="lg:w-64 lg:flex-shrink-0">
            <div className="sticky top-24">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2 pl-4">{pageTitle}</h3>
              <p className="text-xs text-slate-400 mb-4 pl-4">{fullLastUpdatedText}</p>
              <SideNavigation
                sections={navItems.map(item => ({ ...item, icon: renderIcon(item.icon as string) }))}
                activeSectionId={activeSectionId}
                onNavigate={handleNavigation}
                isMobileLayout={false}
                variant="vertical"
              />
            </div>
          </aside>
          <main className="lg:flex-grow min-w-0 bg-white p-8 md:p-12 rounded-xl border-2 border-black shadow-[8px_8px_0_0_rgba(0,0,0,1)]">
            {childrenWithRefs}
          </main>
        </div>
      </div>
    </div>
  );
};
